/* CLEAN SCAN: AXES ALIGMENT RENDERER */
import React, { useState, useEffect } from 'react';
import useEditorStore from '../../stores/useEditorStore';
import { useWorkflowActions } from '../../stores/useWorkflowStore';
import { useAxesAlignmentStore } from '../../stores';
import { useShallow } from 'zustand/shallow';
import { useNavigate } from 'react-router-dom';

// Static configuration for the five landmarks
export const landmarksConfig = [
  { id: 'subPatellar', label: 'Sub patellar', color: '#10b981', compColor: '#10b98144' },
  { id: 'midPatellar', label: 'Mid patellar', color: '#ef4444', compColor: '#ef444444' },
  { id: 'frontEnd', label: '50/50 Front end', color: '#f59e0b', compColor: '#f59e0b44' },
  { id: 'internalCondyle', label: '50/50 Internal condyle', color: '#3b82f6', compColor: '#3b82f644' },
  { id: 'externalCondyle', label: '50/50 External condyle', color: '#01DCE6', compColor: '#00A8E644' },
];

const PinToolCard = () => {
  const navigate = useNavigate();
  const { completeCurrentStep } = useWorkflowActions();

  // pull from zustand store
  const [ selections, activeKey, setActiveKey, resetPoint, savePoint, clearSelections, ] = useAxesAlignmentStore(
    useShallow((state) => [
      state.selections,
      state.activeKey,
      state.setActiveKey,
      state.resetPoint,
      state.savePoint,
      state.clearSelections,
    ])
);

  // get pin mode from editor store
  const [pinMode, setToolSetting] = useEditorStore(
    useShallow((state) => [
      state.toolSettings.scan_axes_pin?.mode || 'select',
      state.setToolSetting
    ])
  );

  useEffect(() => {
    //On mount, select the uncompleted point
    if (!activeKey) {
      const uncompletedPoints = Object.keys(selections).filter(key => !selections[key].completed);
      if (uncompletedPoints.length > 0) {
        setActiveKey(uncompletedPoints[0]);
      }
    }
  }, [selections, activeKey, setActiveKey]);

  const handleItemClick = (id) => {
    const isCompleted = selections[id].completed;
    if (!isCompleted) {
      setActiveKey(id);
    }
  };

  const handleDelete = (id) => {
    resetPoint(id);
    setActiveKey(id);
  };

  const handleConfirm = () => {
    const nextRoute = completeCurrentStep();
    if (nextRoute) navigate(nextRoute);
  };

  const allItemsSet = Object.values(selections).every((s) => s.completed);

  return (
    <div className="absolute top-[10px] left-[10px] bg-white/95 rounded-lg p-4 shadow-[0_2px_8px_rgba(0,0,0,0.1)] min-w-[250px] z-[1000]">
      {/* Title */}
      <div className="flex items-center mb-4 pb-3 border-b border-gray-200">
        <div className="flex w-4 h-4 place-items-center justify-center rounded-full border-2 border-violet-600 mr-2">
          <div className="w-2 h-2 rounded-full bg-violet-600" />
        </div>
        <span className="text-violet-600 font-semibold text-base">Landmarks</span>
      </div>

      {/* Landmark Items */}
      <div className="flex flex-col gap-2 mb-4">
        {landmarksConfig.map((item) => {
          const { id, label, color, compColor } = item;
          const isSet = selections[id].completed;
          const isActive = activeKey === id;

          return (
            <div
              key={id}
              onClick={() => handleItemClick(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderRadius: '6px',
                background: isSet ? compColor : '#ffffff',
                border: isActive ? '2px solid #7c3aed' : '1px solid #e5e7eb',
                cursor: isSet ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: isActive ? 0.7 : 1,
              }}
              onMouseOver={(e) => {
                if (!isSet && !isActive) e.target.style.borderColor = '#7c3aed';
              }}
              onMouseOut={(e) => {
                if (!isSet && !isActive) e.target.style.borderColor = '#e5e7eb';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    marginRight: '12px',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: isSet ? '600' : '500',
                  }}
                >
                  {label}
                </span>
              </div>

              {isSet && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(id);
                  }}
                  style={{ padding: '4px', borderRadius: '4px', cursor: 'pointer' }}
                  onMouseOver={(e) => (e.target.style.backgroundColor = '#fee2e2')}
                  onMouseOut={(e) => (e.target.style.backgroundColor = 'transparent')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PinToolCard;
